import React, { useState, useCallback, useMemo, useEffect } from 'react';
import ReactFlow, { 
  addEdge, 
  Background, 
  Controls, 
  applyEdgeChanges, 
  applyNodeChanges,
  useReactFlow 
} from 'reactflow';
import dagre from 'dagre';
import 'reactflow/dist/style.css';
import { 
  Save, Trash2, X, ChevronDown, ChevronUp, Zap, 
  GitBranch, ChevronLeft, ChevronRight, Bot, AlertTriangle, Plus, LayoutGrid, Globe, Copy, ExternalLink
} from 'lucide-react';
import api from '../../../services/api';
import { apiDefinitions } from '../Bot/apiDefinitions';
import BotNode from './BotNode';
import CustomEdge from '../Bot/CustomEdge'; 
import toast from 'react-hot-toast';

import '../styles/BotFlow.css';
import '../Bot/styles/CustomNode.css';
import '../Bot/styles/ButtonModal.css';

const nodeTypes = { botNode: BotNode };
const edgeTypes = { custom: CustomEdge };

const getLayoutedElements = (nodes, edges, direction = 'TB') => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: direction, nodesep: 70, ranksep: 100 });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: 260, height: 120 });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  return {
    nodes: nodes.map((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      return {
        ...node,
        position: { x: nodeWithPosition.x - 130, y: nodeWithPosition.y - 60 },
      };
    }),
    edges,
  };
};

const BotFlowEditor = ({ estabelecimentoId }) => {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [listaBots, setListaBots] = useState([]); 
  const [selectedNodeId, setSelectedNodeId] = useState(null); 
  const [nomeFluxo, setNomeFluxo] = useState("Novo Robô");
  const [fluxoId, setFluxoId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [proximoIdSequencial, setProximoIdSequencial] = useState(10001);
  const [deleteModal, setDeleteModal] = useState({ show: false, botId: null, botNome: "" });
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [menuOpen, setMenuOpen] = useState({ acoes: false, condicoes: false, meusBots: true });
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);

  // URL Base para o Webhook de Entrada
  const webhookUrlBase = `${window.location.origin}/api/whatsapp/webhook/entrada/${estabelecimentoId}`;

  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedNodeId) || null,
    [nodes, selectedNodeId]
  );

  const onNodesChange = useCallback((changes) => setNodes((nds) => applyNodeChanges(changes, nds)), []);
  const onEdgesChange = useCallback((changes) => setEdges((eds) => applyEdgeChanges(changes, eds)), []);

  const onLayout = useCallback((direction = 'TB') => {
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(nodes, edges, direction);
    setNodes([...layoutedNodes]);
    setEdges([...layoutedEdges]);
    toast.success("Fluxo organizado!");
  }, [nodes, edges]);

  const fetchListaBots = useCallback(async () => {
    try {
      const res = await api.get(`/whatsapp/bot/fluxos/${estabelecimentoId}`);
      const data = res.data?.payload || res.data || [];
      setListaBots(data);
      return data;
    } catch (err) { return []; }
  }, [estabelecimentoId]);

 const loadSpecificBot = async (bot) => {
    if (!bot?.id) return;
    try {
      setLoading(true);
      setSelectedNodeId(null);
      setFluxoId(bot.id);
      setNomeFluxo(bot.nome_fluxo);
      
      const passosRes = await api.get(`/whatsapp/bot/fluxo-passos/${bot.id}`);
      const passos = passosRes.data?.payload || passosRes.data || [];

      // 1. Criamos os nós primeiro
      const savedNodes = passos.map((p) => ({
        id: `node_${p.id}`,
        type: 'botNode',
        position: { x: 250, y: 50 },
        data: {
          label: p.description,
          type: p.tipo,
          action_id: p.action_id,
          condition_id: p.condition_id,
          identifier: p.identifier,
          parameters: typeof p.parametros === 'string' ? JSON.parse(p.parametros) : (p.parametros || []),
          onEdit: (id) => setSelectedNodeId(id)
        }
      }));

      // 2. Criamos as conexões (edges)
      const savedEdges = [];
      savedNodes.forEach(child => {
        const identifier = child.data.identifier;
        if (identifier && identifier.includes('.')) {
          const parts = identifier.split('.');
          const handleType = parts.pop(); // ex: 'true', 'false', 'next'
          const parentIdentifier = parts.join('.');
          
          // Busca o nó pai pelo identifier dele
          const parent = savedNodes.find(n => n.data.identifier === parentIdentifier);
          
          if (parent) {
            savedEdges.push({
              id: `e-${parent.id}-${child.id}`,
              source: parent.id,
              target: child.id,
              sourceHandle: handleType,
              type: 'custom',
              animated: true,
              style: { 
                stroke: handleType === 'true' ? '#00FF00' : handleType === 'false' ? '#FF0000' : '#00a884', 
                strokeWidth: 2 
              },
              data: { 
                tipo: handleType, 
                label: handleType === 'true' ? 'SIM' : handleType === 'false' ? 'NÃO' : '', 
                onDelete: (id) => setEdges(eds => eds.filter(e => e.id !== id)) 
              }
            });
          }
        }
      });

      const layouted = getLayoutedElements(savedNodes, savedEdges);
      setNodes(layouted.nodes);
      setEdges(layouted.edges);
    } catch (err) { 
      toast.error("Erro ao carregar"); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleNovoBot = () => {
    setFluxoId(null); setNomeFluxo("Novo Robô"); setEdges([]);
    setNodes([{ id: '10000', type: 'botNode', data: { label: 'Início', type: 1, parameters: [], identifier: '10000' }, position: { x: 250, y: 50 }, deletable: false }]);
    setLoading(false);
  };

  useEffect(() => {
    const init = async () => {
      const bots = await fetchListaBots();
      if (bots?.length > 0) loadSpecificBot(bots[0]);
      else handleNovoBot();
    };
    init();
  }, [fetchListaBots]);

  const onConnect = useCallback((params) => {
    setNodes((nds) => {
      const sourceNode = nds.find(n => n.id === params.source);
      const targetNode = nds.find(n => n.id === params.target);
      if (!sourceNode || !targetNode) return nds;

      const parentId = sourceNode.data.identifier || "10000";
      const handle = params.sourceHandle ? `.${params.sourceHandle}` : '';
      const novoId = `${parentId}${handle}.${targetNode.id.replace('node_', '')}`;

      const newNodes = nds.map(n => n.id === params.target ? { ...n, data: { ...n.data, identifier: novoId } } : n);
      
      setEdges((eds) => addEdge({
        ...params, type: 'custom', animated: true,
        style: { stroke: params.sourceHandle === 'true' ? '#00FF00' : params.sourceHandle === 'false' ? '#FF0000' : '#00a884', strokeWidth: 2 },
        data: { tipo: params.sourceHandle || 'linear', label: params.sourceHandle === 'true' ? 'SIM' : params.sourceHandle === 'false' ? 'NÃO' : '', onDelete: (id) => setEdges(e => e.filter(ed => ed.id !== id)) }
      }, eds));
      return newNodes;
    });
  }, []);

  const addNode = (item, isCondition = false) => {
    const novoId = proximoIdSequencial.toString();
    setNodes(nds => nds.concat({
      id: `node_${novoId}`, type: 'botNode', position: { x: 400, y: 150 },
      data: { 
        label: item.label, action_id: isCondition ? null : item.apiId, condition_id: isCondition ? item.apiId : null,
        type: isCondition ? 3 : 2, identifier: novoId,
        parameters: (item.parameters || []).map(p => ({ id: p.id, value: p.defaultValue || '', name: p.name, type: p.type || 'text', options: p.options || [] })),
        onEdit: (id) => setSelectedNodeId(id)
      }
    }));
    setProximoIdSequencial(p => p + 1);
  };

  const updateNodeParameter = (nodeId, paramId, value) => {
    setNodes(nds => nds.map(n => n.id === nodeId ? { ...n, data: { ...n.data, parameters: n.data.parameters.map(p => p.id === paramId ? { ...p, value } : p) } } : n));
  };

  const saveFlow = async () => {
    if (!nomeFluxo || nomeFluxo === "Novo Robô") return toast.error("Defina um nome para o Robô.");
    try {
      const passos = nodes.map(n => ({
        identifier: n.data.identifier || n.id,
        type: n.data.type,
        action_id: n.data.action_id,
        condition_id: n.data.condition_id,
        description: n.data.label,
        parameters: n.data.parameters || [],
        open: true
      }));

      const res = await api.post(`/whatsapp/bot/save-flow/${estabelecimentoId}`, {
        fluxoId, nome_flow: nomeFluxo, passos,
        gatilho: { tipo: 'texto', valor: nomeFluxo.toLowerCase().trim() }
      });

      if (res.data.success) {
        toast.success("Robô Salvo!");
        setFluxoId(res.data.fluxoId);
        fetchListaBots();
      }
    } catch (e) { toast.error("Erro ao salvar"); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado para a área de transferência!");
  };

  // --- FUNÇÃO AUXILIAR PARA INSERIR VARIÁVEL NO TEXTAREA ---
  const insertVariable = (nodeId, paramId, variable) => {
    setNodes(nds => nds.map(n => {
      if (n.id === nodeId) {
        return {
          ...n,
          data: {
            ...n.data,
            parameters: n.data.parameters.map(p => 
              p.id === paramId ? { ...p, value: (p.value || "") + `{{${variable}}}` } : p
            )
          }
        };
      }
      return n;
    }));
  };

  const renderInput = (param) => {
    const commonProps = { 
      value: param.value || "", 
      onChange: (e) => updateNodeParameter(selectedNode.id, param.id, e.target.value) 
    };

    // --- TRATAMENTO PARA WEBHOOK DE SAÍDA (AÇÃO 11) ---
    // Adicionamos atalhos para n8n receber dados dinâmicos
    if (selectedNode?.data?.action_id === 11 && param.id === 'body') {
      return (
        <div className="webhook-field">
          <div className="flex flex-wrap gap-1 mb-2">
            {['nome_cliente', 'ultima_resposta', 'telefone', 'pedido_id'].map(v => (
              <button 
                key={v}
                type="button"
                className="px-2 py-1 text-[10px] bg-slate-700 hover:bg-indigo-600 rounded border border-slate-600 text-white transition-colors"
                onClick={() => insertVariable(selectedNode.id, param.id, v)}
              >
                +{v}
              </button>
            ))}
          </div>
          <textarea 
            rows={6} 
            className="editor-input font-mono text-sm bg-black text-emerald-400 p-2 rounded" 
            {...commonProps} 
            placeholder='{"msg": "{{ultima_resposta}}"}' 
          />
          <small className="text-gray-400 mt-1 block">JSON que o n8n receberá.</small>
        </div>
      );
    }

    // --- TRATAMENTO PARA WHATSAPP TEMPLATES ---
    if (param.id === 'templatewhatsapp') {
      return (
        <select className="editor-select" {...commonProps}>
          <option value="">Selecione um template...</option>
          {whatsappTemplates.map((t, i) => (
            <option key={i} value={t.value}>{t.title}</option>
          ))}
        </select>
      );
    }

    // --- TRATAMENTO PARA TEXTO SIMPLES (AÇÃO 1) ---
    if (param.id === 'Parametro1' && selectedNode?.data?.action_id === 1) {
      return (
        <div className="message-field">
          <div className="flex flex-wrap gap-1 mb-2">
            {['nome_cliente', 'ultima_resposta'].map(v => (
              <button 
                key={v}
                type="button"
                className="px-2 py-1 text-[10px] bg-slate-700 hover:bg-emerald-600 rounded border border-slate-600 text-white"
                onClick={() => insertVariable(selectedNode.id, param.id, v)}
              >
                +{v}
              </button>
            ))}
          </div>
          <textarea 
            rows={4} 
            className="editor-input" 
            value={typeof param.value === 'object' ? param.value[0]?.text : param.value} 
            onChange={e => updateNodeParameter(selectedNode.id, param.id, e.target.value)} 
          />
        </div>
      );
    }

    // --- RENDER PADRÃO POR TIPO ---
    switch (param.type) {
      case 'select':
      case 'options':
        return (
          <select className="editor-select" {...commonProps}>
            <option value="">Selecione...</option>
            {param.options?.map(o => (
              <option key={o.value} value={o.value}>{o.title || o.label}</option>
            ))}
          </select>
        );
      case 'textarea':
        return <textarea rows={4} className="editor-input" {...commonProps} />;
      default:
        return <input type="text" className="editor-input" {...commonProps} />;
    }
  };
  
  
  if (loading) return <div className="loading-container">Carregando Fluxo...</div>;

  return (
    <div className="bot-flow-container">
      {/* MODAL EXCLUSÃO - OMITIDO POR BREVIDADE, MANTENHA O SEU */}

      <div className="bot-flow-header">
        <div className="header-left">
          <button className="btn-icon-round" onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
            {isSidebarOpen ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
          </button>
          <div className="flow-title-area">
             <Bot size={20} className="text-emerald-500" />
             <input className="flow-name-input" value={nomeFluxo} onChange={e => setNomeFluxo(e.target.value)} />
          </div>
        </div>
        
        {/* WIDGET DO ENDPOINT DE ENTRADA */}
        <div className="webhook-endpoint-badge" onClick={() => copyToClipboard(webhookUrlBase)}>
            <Globe size={14} className="text-blue-400" />
            <span className="text-xs font-mono truncate max-w-[200px]">{webhookUrlBase}</span>
            <Copy size={12} className="ml-2 cursor-pointer hover:text-white" />
        </div>

        <div className="flex gap-2">
           <button className="btn-save-flow" style={{ backgroundColor: '#6366f1' }} onClick={() => onLayout('TB')}>
             <LayoutGrid size={18} /> Organizar
           </button>
           <button className="btn-save-flow" onClick={saveFlow}><Save size={18} /> Salvar Robô</button>
        </div>
      </div>

      <div className="bot-flow-wrapper">
        <aside className={`bot-flow-sidebar ${isSidebarOpen ? 'open' : 'closed'}`}>
          <div className="sidebar-content custom-scrollbar">
            <button className="btn-new-bot-main" onClick={handleNovoBot}><Plus size={18} /> Novo Robô</button>
            
            {/* SEÇÃO MEUS BOTS */}
            <div className="sidebar-section">
              <div className="section-header" onClick={() => setMenuOpen(p => ({ ...p, meusBots: !p.meusBots }))}>
                <Bot size={16} /> MEUS BOTS {menuOpen.meusBots ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
              </div>
              {menuOpen.meusBots && listaBots.map(bot => (
                <div key={bot.id} className={`bot-list-item ${fluxoId === bot.id ? 'active' : ''}`} onClick={() => loadSpecificBot(bot)}>
                  <span>{bot.nome_fluxo}</span>
                  <Trash2 size={14} className="hover:text-red-500" onClick={(e) => { e.stopPropagation(); setDeleteModal({ show: true, botId: bot.id, botNome: bot.nome_fluxo }); }} />
                </div>
              ))}
            </div>

            {/* SEÇÃO AÇÕES */}
            <div className="sidebar-section">
              <div className="section-header" onClick={() => setMenuOpen(p => ({ ...p, acoes: !p.acoes }))}>
                <Zap size={16} className="text-emerald-400" /> AÇÕES {menuOpen.acoes ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
              </div>
              {menuOpen.acoes && apiDefinitions.acoes.map(a => (
                <div key={a.apiId} className="sidebar-item-card" onClick={() => addNode(a)}>
                  {a.apiId === 11 && <Globe size={12} className="mr-2 inline" />}
                  {a.label}
                </div>
              ))}
            </div>

            {/* SEÇÃO CONDIÇÕES */}
            <div className="sidebar-section">
              <div className="section-header" onClick={() => setMenuOpen(p => ({ ...p, condicoes: !p.condicoes }))}>
                <GitBranch size={16} className="text-pink-400" /> CONDIÇÕES {menuOpen.condicoes ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
              </div>
              {menuOpen.condicoes && apiDefinitions.condicoes.map(c => (
                <div key={c.apiId} className="sidebar-item-card condition" onClick={() => addNode(c, true)}>{c.label}</div>
              ))}
            </div>
          </div>
        </aside>

        <div className="flow-canvas">
          <ReactFlow nodes={nodes} edges={edges} nodeTypes={nodeTypes} edgeTypes={edgeTypes} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect} onNodeClick={(e, n) => setSelectedNodeId(n.id)} fitView>
            <Background color="#ccc" gap={20} />
            <Controls />
          </ReactFlow>
        </div>

        {selectedNode && (
          <aside className="bot-node-editor open">
            <div className="editor-header-modern">
                <div className="header-info"><span className="node-id-badge">ID: {selectedNode.data?.identifier}</span><h3>{selectedNode.data?.label}</h3></div>
                <button className="btn-close-editor" onClick={() => setSelectedNodeId(null)}><X size={20}/></button>
            </div>
            <div className="editor-content custom-scrollbar">
              {selectedNode.data?.parameters?.map(p => (
                <div key={p.id} className="editor-field-group">
                  <label>{p.name}</label>
                  {renderInput(p)}
                </div>
              ))}
              <div className="editor-footer">
                {selectedNode.id !== '10000' && <button className="btn-delete-node" onClick={() => { setNodes(nds => nds.filter(n => n.id !== selectedNode.id)); setEdges(eds => eds.filter(e => e.source !== selectedNode.id && e.target !== selectedNode.id)); setSelectedNodeId(null); }}><Trash2 size={16} /> Deletar Etapa</button>}
                <button className="btn-confirm-node" onClick={() => setSelectedNodeId(null)}>Fechar</button>
              </div>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
};

export default BotFlowEditor;